import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useUser } from "../contexts/UserContext";
import { useBrand } from "../contexts/BrandContext";
import { MIN_PASSWORD_LENGTH, PASSWORD_POLICY_TEXT, passwordProblems, passwordRuleStatus } from "@tradesmatepro/shared-ui";
import { normalizePhone, isPlausiblePhone } from "../utils/phone";
import { isValidEmail, friendlyDbError } from "../utils/email";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import PhoneField from "../components/Common/PhoneField";

/**
 * Required-field marker.
 *
 * Every field on this form except Phone was required, and none of them said so — only
 * "Phone (optional)" carried any marker, which left the user to infer the rule backwards
 * from the single exception. The `required` attribute was present on the inputs, so the
 * browser enforced it, but nothing on screen told you before you submitted.
 *
 * aria-hidden on the asterisk because the input's own `required` attribute is what a
 * screen reader announces; reading "star" after every label would be noise on top of it.
 */
const RequiredMark = () => (
  <span aria-hidden="true" className="text-red-400 ml-0.5">*</span>
);

export default function Signup() {
  const navigate = useNavigate();
  // #4962: `loading` is true until UserContext has resolved the Supabase session, and the
  // redirect below only fires once `user` exists. Rendering the form in that window meant a
  // signed-in user briefly saw a signup form before being sent to the dashboard. Gated below,
  // the same way ProtectedRoute and StrictAuthGuard wait on this flag.
  const { user, loading: authLoading } = useUser();
  const { brand } = useBrand();
  // Signup had NO reveal at all while Login has one, so the form that ENFORCES
  // complexity rules was the one you could not read back. A user who is told
  // "must include an uppercase letter" and cannot see what they typed is being asked
  // to guess.
  const [showPasswords, setShowPasswords] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Board #311: bring the message to the user rather than hoping they scroll up.
  const errorRef = useRef(null);
  useEffect(() => {
    if (!error || !errorRef.current) return;
    errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus so a screen reader lands on it too; tabIndex={-1} keeps it out of tab order.
    errorRef.current.focus({ preventScroll: true });
  }, [error]);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    legalAccepted: false
  });

  // #5743: say it under the field as soon as the confirmation has diverged (or is complete
  // and different), instead of only after submit.
  const confirmMismatch = !!formData.confirmPassword && (
    !formData.password.startsWith(formData.confirmPassword)
    || formData.confirmPassword.length >= formData.password.length
  ) && formData.confirmPassword !== formData.password;

  // Redirect if already logged in.
  // #4968: mirrors Login.js — a signed-in user still on a temporary password belongs on
  // /set-password, and nothing downstream (ProtectedRoute) enforces that flag, so landing on
  // /signup was the one way to skip it.
  useEffect(() => {
    if (user) {
      navigate(user.must_change_password === true ? '/set-password' : '/dashboard');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: value
    }));
    // The banner goes stale the moment you start fixing what it complains about. setError
    // only ran at the top of handleSubmit, so "Password must include a number." stayed on
    // screen while the user typed a compliant password, and only cleared on the NEXT
    // submit — the field looked rejected when it was already fine.
    //
    // Guarded on `error` so an untouched form does not re-render on every keystroke.
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation — collect EVERY problem, then report them together.
    //
    // This used to `return` on the first failure, so a user with three empty fields was
    // told about one, fixed it, submitted, and was told about the next. Signup is the
    // first thing anyone does in this product and it was drip-feeding its objections.
    const problems = [];

    if (!formData.companyName.trim()) problems.push("Company name is required.");
    // Lengths are checked here as well as via maxLength, because the attribute only
    // constrains the keyboard: it does not survive a scripted submit, and companies.name,
    // users.first_name and users.last_name are all unbounded `text`. A 200-character
    // company and a 500-character first name are already in the live tables. These names
    // are rendered into invoice and quote PDFs, where they have to lay out.
    else if (formData.companyName.trim().length > 120) {
      problems.push("Company name is too long (120 characters maximum).");
    }
    if (formData.firstName.trim().length > 60 || formData.lastName.trim().length > 60) {
      problems.push("Name is too long (60 characters maximum).");
    }
    if (!formData.firstName.trim()) problems.push("First name is required.");
    if (!formData.lastName.trim()) problems.push("Last name is required.");
    // Format is checked HERE, against the same pattern the column's CHECK constraint
    // uses. Without it `notreal@notld` passed the form and came back from Postgres as
    // `violates check constraint "chk_companies_email_format"`, shown to the user
    // verbatim — a database internal in a signup form.
    if (!formData.email.trim()) {
      problems.push("Email is required.");
    } else if (!isValidEmail(formData.email)) {
      problems.push("That email address is not valid. Check for a missing domain ending, like .com.");
    }
    // Board #3578: required here because onboarding requires it one screen later
    // (CompanyBasicsStep.js:539) — this form called it optional and the next one refused to
    // continue without it. What is typed here carries over: signup writes companies.phone
    // and onboarding pre-fills from it.
    //
    // type="tel" restricts nothing — it only hints the mobile keyboard — so letters reached
    // the column unchallenged. This is the same field-bleed family as the customer saved with
    // phone "+112096025553300" and both address columns null.
    if (!formData.phone.trim()) {
      problems.push("Phone is required — it's how customers reach your business.");
    } else if (!isPlausiblePhone(formData.phone)) {
      problems.push("Phone number doesn't look like a phone number.");
    }
    // Policy lives in shared-ui, not here. This surface used to require 6 characters with no
    // complexity rule, which made account creation the WEAKEST password gate in the product —
    // SetPassword, MyProfile and the customer-portal signup all already required 8.
    problems.push(...passwordProblems(formData.password));
    // Only worth saying once the password itself is valid — "passwords do not match" on
    // top of "password is too short" is two complaints about one unfinished field.
    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      problems.push("Passwords do not match.");
    }
    if (!formData.legalAccepted) {
      problems.push("You must agree to the Terms of Service and Privacy Policy.");
    }

    if (problems.length > 0) {
      setError(problems.join(" "));
      return;
    }

    setLoading(true);

    try {
      // Call the Supabase Edge Function to create company + owner
      // This runs server-side with service role key
      const { data, error: fnError } = await supabase.functions.invoke('create-company-with-owner', {
        body: {
          companyName: formData.companyName.trim(),
          ownerFirstName: formData.firstName.trim(),
          ownerLastName: formData.lastName.trim(),
          ownerEmail: formData.email.trim().toLowerCase(),
          // Normalized, not raw: the edge function writes this to BOTH users.phone and
          // companies.phone without checking it, so whatever shape leaves here is what the
          // database keeps forever.
          ownerPhone: normalizePhone(formData.phone),
          tempPassword: formData.password,
          legalAccepted: formData.legalAccepted
        }
      });

      if (fnError) {
        console.error('Edge function error:', fnError);
        // supabase.functions.invoke returns the fixed wrapper string ("Edge Function
        // returned a non-2xx status code") on any 4xx; the server's real reason
        // (e.g. "email already registered") is in the response body. Surface that.
        let serverMsg = null;
        try { serverMsg = (await fnError.context?.json?.())?.error; } catch (_) { /* non-JSON body */ }
        if (serverMsg && /already.*(registered|exists)|duplicate/i.test(serverMsg)) {
          serverMsg = 'This email is already registered. Try logging in instead.';
        } else if (serverMsg) {
          // Backstop for anything the client checks did not catch. A raw Postgres
          // message reached the red box verbatim — constraint names and all — so
          // anything recognisable is rewritten before it is shown. The client-side
          // checks above should mean a user never gets here, but a second line of
          // defence costs nothing and the failure mode it prevents is a schema leak.
          serverMsg = friendlyDbError(serverMsg) || serverMsg;
        }
        throw new Error(serverMsg || fnError.message || 'Failed to create account');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Account creation failed');
      }

      console.log('✅ Account created successfully:', data);

      // Now sign in with the newly created credentials
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (signInError) {
        // Account created but auto-login failed - still success, just redirect to login
        console.warn('Auto-login failed, redirecting to login:', signInError);
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Success! Redirect to dashboard
      navigate("/dashboard");

    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || "Account creation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // #4962/#4968: also while a session EXISTS. The redirect runs in an effect, i.e. after this
  // render, so gating on authLoading alone still painted the form for a frame once the user
  // resolved (or on an in-app navigation here). A signed-in user is leaving; show the spinner.
  if (authLoading || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="w-full max-w-md p-8 rounded-2xl shadow-2xl backdrop-blur-xl border border-white/10" style={{
        background: "rgba(30, 41, 59, 0.7)",
        backdropFilter: "blur(15px)"
      }}>
        <h1 className="text-3xl font-bold text-white text-center mb-4">
          Create Your {brand.name} Account
        </h1>
        <p className="text-white/90 text-center mb-8">
          Set up your company and owner account to get started
        </p>

        {/* Board #311. The message was always CORRECT — passwordProblems rejects a missing
            uppercase or digit and sets it before the network call is ever made. It was just
            never seen: this banner sits above the form, and by the time you have filled in
            every field and pressed Create Account the page is scrolled to the button, which
            is a screen or more below. The tester concluded the weak password was silently
            accepted, which is exactly what it looks like.

            role="alert" + tabIndex makes it announceable and focusable; the effect below
            brings it into view. Fixing VISIBILITY rather than the message, and deliberately
            not touching the server-error handler at :197 that shares this state. */}
        {error && (
          <div
            ref={errorRef}
            role="alert"
            tabIndex={-1}
            className="mb-4 p-3 rounded-md bg-red-500/80 text-white text-sm outline-none"
          >
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 rounded-md bg-green-500/80 text-white text-sm">
            ✅ Account created successfully! Redirecting to login...
          </div>
        )}

        {/* Company Name, First/Last Name, Email and Phone each had a <label> sitting next to an
            <input> with nothing joining them — no htmlFor, no id — so a screen reader announced
            five of the seven fields on the app's front door as unnamed. Password and Confirm
            Password were already wired, which is where the `signup-*` id convention comes from;
            the Terms checkbox wraps its own input, so it is implicitly associated and needed
            nothing. Verified after wiring: 7 htmlFor values, 7 matching ids, no orphan either way.

            Same WCAG 4.1.2 class closed on Vendors, Rates & Pricing, Quote Acceptance and
            Security. Static ids rather than useId because these are singletons on a page, not
            instances of a repeated component. */}

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-xs text-gray-400">
            <RequiredMark /> Required
          </p>
          {/* Company Name */}
          <div>
            <label htmlFor="signup-company-name" className="text-gray-200 font-medium text-sm">Company Name<RequiredMark /></label>
            <input
              type="text"
              id="signup-company-name"
              name="companyName"
              autoComplete="organization"
              maxLength={120}
              required
              value={formData.companyName}
              onChange={handleChange}
              className="w-full mt-1 px-4 py-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {/* maxLength stops the 121st character SILENTLY — paste 200 and 80 vanish with
                no counter and no message, which reads as the field eating your input. The
                cap is correct; only the feedback was missing. Shown from 100 so it appears
                before it starts mattering rather than at the moment text disappears. */}
            {formData.companyName.length >= 100 && (
              <p className={`mt-1 text-xs ${formData.companyName.length >= 120 ? 'text-amber-300' : 'text-gray-400'}`}>
                {formData.companyName.length} / 120 characters
                {formData.companyName.length >= 120 && ' — maximum reached'}
              </p>
            )}
          </div>

          {/* First + Last Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="signup-first-name" className="text-gray-200 font-medium text-sm">First Name<RequiredMark /></label>
              <input
                type="text"
                id="signup-first-name"
                name="firstName"
                autoComplete="given-name"
                maxLength={60}
                required
                value={formData.firstName}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="signup-last-name" className="text-gray-200 font-medium text-sm">Last Name<RequiredMark /></label>
              <input
                type="text"
                id="signup-last-name"
                name="lastName"
                autoComplete="family-name"
                maxLength={60}
                required
                value={formData.lastName}
                onChange={handleChange}
                className="w-full mt-1 px-4 py-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="signup-email" className="text-gray-200 font-medium text-sm">Email<RequiredMark /></label>
            {/* title = the full address, so a long one can be read back on hover.
                There is no layout fix available here and none was attempted: this input is already
                `w-full` in its own row — unlike First/Last Name above, it is not sharing the
                two-column grid — so its ~382px is the width of the form itself. Any address long
                enough to overflow that will overflow whatever the form is; browsers scroll the
                input to the caret, so the tail is visible while typing and the head is not
                recoverable afterwards. The gap the finding identifies is verification AFTER typing,
                which is what a tooltip closes.
                Deliberately not a maxLength: RFC 5321 allows 254 characters and the addresses that
                triggered this were legitimate. Truncating a real address at signup would turn a
                display problem into a failed account. */}
            <input
              type="email"
              id="signup-email"
              name="email"
              autoComplete="email"
              required
              title={formData.email || undefined}
              value={formData.email}
              onChange={handleChange}
              className="w-full mt-1 px-4 py-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Phone — the same control the Customers form uses, not a third hand-rolled one.
              This was a plain box with a US-only placeholder, so an international contractor
              was never told which country was being assumed and had no way to say otherwise.
              PhoneField carries the picker, formats as you type, flags letters immediately
              and hands back one normalised E.164 value. Required (board #3578) — see validate.
              #4983: the class props mirror the inputs above. PhoneField's defaults are
              theme-driven (bg-white/70 dark:…) and this page is hardcoded dark, so without
              them the phone box rendered white in a light-theme session. */}
          <div>
            <PhoneField
              label={<>Phone<RequiredMark /></>}
              ariaLabel="Phone"
              required
              value={formData.phone}
              onChange={(phone) => setFormData(prev => ({ ...prev, phone }))}
              error={
                formData.phone.trim() !== '' && !isPlausiblePhone(formData.phone)
                  ? "That doesn't look like a complete phone number yet."
                  : ''
              }
              testId="signup-phone"
              autoComplete="tel"
              labelClassName="block text-gray-200 font-medium text-sm"
              inputClassName="flex-1 min-w-0 mt-1 px-4 py-2 rounded-r-md bg-slate-700/50 border border-l-0 border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              countryButtonClassName="flex items-center gap-1 px-3 py-2 mt-1 h-auto w-[9rem] rounded-l-md bg-slate-700/50 border border-slate-600/50 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="signup-password" className="text-gray-200 font-medium text-sm">Password<RequiredMark /></label>
          <div className="relative mt-1">
            <input
              id="signup-password"
              type={showPasswords ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              aria-describedby="signup-password-policy"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 pr-10 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(v => !v)}
              // #3589: two buttons shared one name, so a screen reader could not tell them apart.
              // Each names its own field and points at it; one toggle still reveals both.
              aria-label={showPasswords ? 'Hide password' : 'Show password'}
              aria-controls="signup-password signup-confirm-password"
              aria-pressed={showPasswords}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPasswords
                ? <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                : <EyeIcon className="h-5 w-5 text-gray-400" />}
            </button>
          </div>
            {/* Stated up front rather than only as a submit-time rejection. A complexity rule
                the user discovers by failing is a rule that reads as the form being broken.
                Once they start typing the sentence becomes a live checklist: the static line
                tells you the rules and never whether you have met them, so the only way to
                find out was to press the button. aria-live so a screen reader hears a rule go
                from unmet to met instead of only hearing the rejection at the end. */}
            {formData.password ? (
              <ul
                id="signup-password-policy"
                aria-live="polite"
                className="mt-2 space-y-1"
              >
                {passwordRuleStatus(formData.password).map(rule => (
                  <li
                    key={rule.id}
                    className={`flex items-center gap-1.5 text-xs ${
                      rule.met ? 'text-green-400' : 'text-gray-400'
                    }`}
                  >
                    <span aria-hidden="true">{rule.met ? '✓' : '○'}</span>
                    <span>{rule.label}</span>
                    {/* The tick is decorative; the state has to be in the text for anyone
                        who cannot see the colour or the glyph. */}
                    <span className="sr-only">{rule.met ? '— met' : '— not yet met'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p id="signup-password-policy" className="mt-1 text-xs text-gray-400">{PASSWORD_POLICY_TEXT}</p>
            )}
          </div>

          {/* Confirm */}
          <div>
            <label htmlFor="signup-confirm-password" className="text-gray-200 font-medium text-sm">Confirm Password<RequiredMark /></label>
          <div className="relative mt-1">
            <input
              id="signup-confirm-password"
              type={showPasswords ? 'text' : 'password'}
              name="confirmPassword"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={formData.confirmPassword}
              onChange={handleChange}
              aria-invalid={confirmMismatch || undefined}
              aria-describedby={confirmMismatch ? 'signup-confirm-mismatch' : undefined}
              className="w-full px-4 py-2 pr-10 rounded-md bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(v => !v)}
              aria-label={showPasswords ? 'Hide password confirmation' : 'Show password confirmation'}
              aria-controls="signup-confirm-password signup-password"
              aria-pressed={showPasswords}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPasswords
                ? <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                : <EyeIcon className="h-5 w-5 text-gray-400" />}
            </button>
          </div>
          {confirmMismatch && (
            <p id="signup-confirm-mismatch" role="alert" className="mt-1 text-xs text-red-400">Passwords do not match.</p>
          )}
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-slate-600/50 bg-slate-800/40 p-3 text-sm text-gray-300">
            <input
              type="checkbox"
              name="legalAccepted"
              required
              checked={formData.legalAccepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500"
            />
            <span>
              I agree to the{' '}
              <a href="/terms.html" target="_blank" rel="noreferrer" className="font-medium text-blue-400 hover:text-blue-300">
                Terms of Service
              </a>{' '}
              and acknowledge the{' '}
              <a href="/privacy.html" target="_blank" rel="noreferrer" className="font-medium text-blue-400 hover:text-blue-300">
                Privacy Policy
              </a>
              . These services are operated by CGRenewables LLC.
            </span>
          </label>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 mt-4 rounded-lg text-white font-semibold text-lg shadow-lg transition bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {/* Login Redirect */}
        <p className="text-center text-gray-400 mt-6">
          Already have an account?{" "}
          {/* A <button>, not a styled <span onClick>. A span without tabIndex is not in the tab
              order and exposes no role, so Enter/Space never fired the handler and a screen
              reader listing links or buttons never encountered it — while blue text and
              cursor-pointer made it read as a link to a sighted mouse user. WCAG 2.1.1 and 4.1.2.
              Matches the reciprocal control in Login.js:218, which was already correct. One source
              line serves both brands (there is no apps/smbmate), so this covers SMBMate too. */}
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-blue-400 hover:text-blue-300 cursor-pointer font-semibold bg-transparent border-none p-0"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
}
